'use client'

import { useEffect, useRef } from 'react'

import { useQueryClient } from '@tanstack/react-query'

import acceptedPoliciesQuery from '@/queries/acceptedPoliciesQuery'
import listingSearchQuery from '@/queries/listingSearchQuery'

/**
 * Refetches the listings search whenever the buyer accepts a new seller policy.
 *
 * The auctions service withholds auction data for sellers whose terms the buyer
 * hasn't accepted, so `augmentListings` gets no auction row and
 * `CombinedListingSearchResult.currentPrice` falls through to 0 — rendered as
 * the empty marker ("—") across the compare panel and the list view.
 *
 * Accepting terms changes what that search would return, but nothing
 * invalidated it: `acceptedPoliciesQuery` uses react-query's defaults
 * (staleTime 0, refetchOnWindowFocus) so it recovers on its own, while
 * `listingSearchQuery`'s 5-minute staleTime suppresses the refetch that would
 * pick up the now-visible pricing. The result was a listing whose CTA had
 * flipped from Apply to Bid while its price stayed "—".
 *
 * Accepting in the listing-details tab invalidates accepted policies in *that*
 * tab's QueryClient only, so this watches the policy data itself rather than
 * hooking the acceptance mutation — that covers the cross-tab path (window
 * focus refetches policies here) and the same-tab path (TermsRestrictions
 * invalidates them directly) with one mechanism.
 */
export const useAcceptedPoliciesSearchSync = (): void => {
  const queryClient = useQueryClient()
  const { data, status } = acceptedPoliciesQuery.useQuery()

  // The policy keys as of the last settled read. `null` until the first
  // success, which only establishes the baseline — invalidating there would
  // fire a duplicate search fetch on every page load.
  const knownPoliciesRef = useRef<Set<string> | null>(null)

  useEffect(() => {
    if (status !== 'success') return

    const current = new Set(Object.keys(data))
    const known = knownPoliciesRef.current
    knownPoliciesRef.current = current

    if (known === null) return

    // Additions only. A policy disappearing doesn't unlock any pricing, and
    // reacting to it would let a flapping response invalidate on every read.
    const gainedPolicy = [...current].some((policy) => !known.has(policy))
    if (!gainedPolicy) return

    void queryClient.invalidateQueries({ queryKey: [listingSearchQuery.name] })
  }, [status, data, queryClient])
}
